import React, { useState, useEffect } from 'react';
import { getDispatcherNotes, updateDispatcherNotes } from '../../services/apiClient/marginalWeatherApi/MarginalWeatherAPI';
import { Button, Typography } from '@mui/material';
import DispatcherNotes from '../../types/DispatcherNotes.types'
import ConfirmUpdateModal from '../appLinks/modal/Modal';
import DispatcherNotesField from '../appLinks/textfield/TextField';
import { useAuth } from '../../context/authContext/AuthContext';
import Header from '../header/Header';
import Footer from '../footer/Footer';
import AvatarSkeleton from '../header/skeleton/AvatarSkeleton';
import { CenteredContainer } from '../appLinks/centered-container/CenteredContainer';
import './MarginalWeather.scss';
import { getErrorMessage, createErrorTelemetryProperties, logError, logInformation, trackError } from '../../utils/AppInsightUtils';

const MarginalWeather: React.FC = () => {
  const { isLoading, userData } = useAuth();
  const [notes, setNotes] = useState<DispatcherNotes>({ Id: 1, Notes: '', UpdatedTime: new Date() });
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleUpdateClick = async () => { handleOpen() };

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        logInformation('[MarginalWeather] [fetchNotes]: Starting to fetch dispatcher notes.', 'MarginalWeather', 'fetchNotes');
        if (userData && !isLoading) {
          const accessToken = userData?.access_token;
          const result = await getDispatcherNotes(accessToken);
          logInformation(`[MarginalWeather] [fetchNotes]: API result successfully retrieved: ${result}`, 'MarginalWeather', 'fetchNotes');
          if (result) {
            const updatedText = extractUpdatedText(result);
            setNotes(prevNotes => ({ ...prevNotes, Notes: updatedText }));
            logInformation('[MarginalWeather] [fetchNotes]: Dispatcher notes updated in state.', 'MarginalWeather', 'fetchNotes');
          }
          else {
            logError(getErrorMessage(new Error(`API call succeeded but returned no data.`)),
              'MarginalWeather',
              'fetchNotes',
              createErrorTelemetryProperties(new Error(`Failed to fetch dispatcher notes`)));
            trackError(new Error(`API call succeeded but returned no data.`));
          }
        }
        else {
          logError(getErrorMessage(new Error(`User data is null or loading: ${isLoading}`)),
            'MarginalWeather',
            'fetchNotes',
            createErrorTelemetryProperties(new Error(`User data is null or loading`)));
          trackError(new Error(`User data is null or loading`));
        }
      } catch (error) {
        handleError(error as Error);
        logError(
          getErrorMessage(new Error(`[MarginalWeather] [fetchNotes]: Failed to fetch dispatcher notes.`)),
          'MarginalWeather',
          'fetchNotes');
        trackError(error as Error);
      }
    };
    if (userData && !isLoading) {
      fetchNotes();
    }
  }, [isLoading, userData]);

  const extractUpdatedText = (result: string): string => {
    let i = 0;
    if (result.includes('UPDATED: ') && result.includes('Updated @')) {
      for (; i !== result.length && result[i] !== '\r' && result[i] !== '\n'; i++);
    }
    if (i !== result.length) {
      for (; i < result.length && !/\w/.test(result[i]); i++);
    }
    return result.substring(i);
  };

  const handleError = (error: Error) => {
    if (error) {
      setError(`Error: ${error.message}`);
    } else {
      setError('An unknown error occurred');
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      logInformation('[MarginalWeather] [handleConfirmUpdate]: Attempting to update dispatcher notes.', 'MarginalWeather', 'handleConfirmUpdate');
      const dispatcherNotes: DispatcherNotes = { Id: 1, Notes: notes.Notes, UpdatedTime: new Date() };
      const accessToken = userData?.access_token as string;
      await updateDispatcherNotes(dispatcherNotes, accessToken);
      logInformation('[MarginalWeather] [handleConfirmUpdate]: Dispatcher notes updated successfully.', 'MarginalWeather', 'handleConfirmUpdate');
      setNotes(dispatcherNotes);
    } catch (error) {
      const errorMessage = getErrorMessage(error as Error);
      logError(`[MarginalWeather] [handleConfirmUpdate]: Failed to update dispatcher notes. Error: ${errorMessage}`, 'MarginalWeather', 'handleConfirmUpdate');
      trackError(error as Error);
      setError(errorMessage ?? 'An unknown error occurred');
    } finally {
      logInformation('[MarginalWeather] [handleConfirmUpdate]: Update operation concluded.', 'MarginalWeather', 'handleConfirmUpdate');
      handleClose();
    }
  };

  return (
    <div className='flex-container'>
      <Header />
      {isLoading ? <AvatarSkeleton />
        : (
          <>
            <div>
              <CenteredContainer>
                {error && <Typography color="error">{error}</Typography>}
                <div className='notes-container'>
                  <DispatcherNotesField
                    notes={notes.Notes}
                    setNotes={setNotes}
                  />
                  <div className='fixed-button-container'>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpdateClick}
                      className='fixed-button'>
                      Update Notes
                    </Button></div>
                </div>
              </CenteredContainer>
            </div>
            <ConfirmUpdateModal
              open={open}
              handleClose={handleClose}
              handleConfirmUpdate={handleConfirmUpdate}
            />
            <Footer />
          </>
        )}
    </div>
  );
};

export default MarginalWeather;
